import { useMemo, useState } from "react";
import { playerIdFor } from "../../../../engine/players";
import { aliasOf, displayName, hasJoined, history } from "../../../../engine/selectors";
import type { Player, PlayerStatus } from "../../../../engine/types";
import { NO_ROW, triesShort, triesWords, type PinRow } from "../../../../shared/pins";
import { Face } from "../../components/Face";
import { ConfirmButton, clockTime, type TabProps } from "./common";
import { Fold } from "./Fold";

/** The four statuses in the words the admin screen uses for them everywhere on this tab. */
const STATUSES: { status: PlayerStatus; label: string }[] = [
  { status: "expected", label: "Not yet" },
  { status: "in", label: "Here" },
  { status: "break", label: "Break" },
  { status: "left", label: "Left" },
];

const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.status, s.label])) as Record<PlayerStatus, string>;

type Filter = "all" | PlayerStatus;

const FILTERS: { filter: Filter; label: string }[] = [
  { filter: "all", label: "All" },
  { filter: "in", label: "Here" },
  { filter: "break", label: "Break" },
  { filter: "expected", label: "Not yet" },
  { filter: "left", label: "Left" },
];

/**
 * A player's PIN on their row (decision 39): whether they have one, and Reset PIN, which logs out
 * every phone on that name; the next phone to pick it sets a new one.
 */
function PinLine({ p, shown, pin, t, say }: { p: Player; shown: string; pin: PinRow; t: TabProps["t"]; say: TabProps["say"] }) {
  const tries = triesWords(pin, t.now(), clockTime);
  return (
    <div className="ad-row ad-player-pin">
      <span className="ad-note ad-grow">{pin.set ? "PIN set" : "No PIN yet"}{tries ? `. ${tries}` : ""}</span>
      <ConfirmButton className="ad-small" label="Reset PIN" disabled={!pin.set && !pin.wrong} yes="Reset it"
        question={`Reset ${shown}'s PIN? Every phone using ${shown}'s name is signed out, and the next phone to pick the name sets a new one.`}
        onConfirm={() => void t.resetPin("player", p.id).then((a) => say(a.ok ? `${shown}'s PIN was reset.` : a.reason ?? "That did not work."))} />
    </div>
  );
}

/**
 * One player, one row: the face, the name, games played and where they are (and any wrong PIN
 * tries). Tapping the row opens it in place to the four status keys, the PIN, and Rename, Remove, More.
 */
function PlayerRow({ p, games, t, say, pin, open, onToggle }: {
  p: Player;
  games: number;
  t: TabProps["t"];
  say: TabProps["say"];
  pin: PinRow;
  open: boolean;
  onToggle: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [more, setMore] = useState(false);
  // The rename box edits the real name; everywhere else shows the name the admin sees.
  const [name, setName] = useState(p.name);
  const shown = displayName(p, "admin");
  const alias = aliasOf(p);
  const joined = hasJoined(p);
  // on a line of its own, short, so the time is never cut off
  const tries = triesShort(pin, t.now(), clockTime);
  return (
    <li className={"ad-player" + (open ? " open" : "")}>
      <button type="button" className="ad-prow" aria-expanded={open} onClick={onToggle}>
        <Face name={shown} src={t.faceOf(p)} />
        <span className="ad-prow-who">
          <span className="ad-prow-name">
            {shown}
            {alias && <span className="ad-alias"> {alias}</span>}
          </span>
          <span className="ad-prow-sub">
            {games} {games === 1 ? "game" : "games"}{p.catchUpCredits > 0 ? `, ${p.catchUpCredits} catch-up` : ""}{joined ? "" : ", no picture"}
          </span>
          {tries && <span className="ad-prow-sub ad-prow-pin">{tries}</span>}
        </span>
        <span className={"ad-pstatus " + p.status}>{STATUS_LABEL[p.status]}</span>
      </button>
      {open && (
        <div className="ad-player-open">
          <div className="ad-chips" role="group" aria-label={`${shown} status`}>
            {STATUSES.map(({ status, label }) => (
              <button key={status} type="button" className={"ad-chip" + (p.status === status ? " on " + status : "")}
                aria-pressed={p.status === status}
                onClick={() => p.status !== status && t.send({ type: "player.status", id: p.id, status })}>
                {label}
              </button>
            ))}
          </div>
          <PinLine p={p} shown={shown} pin={pin} t={t} say={say} />
          {renaming ? (
            <form className="ad-row" onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && name.trim() !== p.name) t.send({ type: "player.renamed", id: p.id, name });
              setRenaming(false);
            }}>
              <input value={name} autoFocus maxLength={24} aria-label={`New name for ${shown}`} onChange={(e) => setName(e.target.value)} />
              <button type="submit" className="btn">Save</button>
              <button type="button" className="btn quiet" onClick={() => { setName(p.name); setRenaming(false); }}>Cancel</button>
            </form>
          ) : (
            <div className="ad-player-head">
              <button type="button" className="ad-small" onClick={() => { setName(p.name); setRenaming(true); }}>Rename</button>
              <ConfirmButton label="Remove" className="ad-small" question={`Remove ${shown}? Only works if they have not played yet.`}
                yes="Remove" onConfirm={() => t.send({ type: "player.removed", id: p.id })} />
              <button type="button" className={"ad-small" + (more ? " on" : "")} aria-expanded={more} onClick={() => setMore(!more)}>More</button>
            </div>
          )}
          {more && !renaming && (
            <div className="ad-stack ad-player-more">
              <ConfirmButton label="Clear picture" disabled={p.photo === 0} question={`Clear ${shown}'s picture? They will need to take a new one.`}
                yes="Clear it" onConfirm={() => t.send({ type: "player.profile", id: p.id, photo: 0 })} />
              <ConfirmButton label="Clear nickname" disabled={p.nickname === null} question={`Clear ${shown}'s nickname${p.nickname ? `, ${p.nickname}` : ""}?`}
                yes="Clear it" onConfirm={() => t.send({ type: "player.profile", id: p.id, nickname: null })} />
              {p.dub && (
                <ConfirmButton label="Remove given name" question={`Remove the name ${shown} was given, ${p.dub.nickname}?`}
                  yes="Remove it" onConfirm={() => t.send({ type: "player.undubbed", id: p.id })} />
              )}
              {!joined && (
                <button type="button" className="btn quiet" onClick={() => t.send({ type: "player.profile", id: p.id, photoWaived: true })}>
                  Let in without a picture
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function PlayersTab({ t, state, say }: TabProps) {
  const [newName, setNewName] = useState("");
  const [find, setFind] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const players = Object.values(state.players).sort((x, y) => displayName(x, "admin").localeCompare(displayName(y, "admin")));
  const games = useMemo(() => history(state).gamesPlayed, [state]);
  const count = (f: Filter) => (f === "all" ? players.length : players.filter((p) => p.status === f).length);
  const notHere = players.filter((p) => p.status === "expected");
  const onBreak = players.filter((p) => p.status === "break");
  const wanted = find.trim().toLowerCase();
  // everyone the search finds, whatever the filter: a name is only new when this is empty
  const found = players.filter((p) => !wanted || [p.name, displayName(p, "admin"), aliasOf(p) ?? ""].some((n) => n.toLowerCase().includes(wanted)));
  const shown = found.filter((p) => filter === "all" || p.status === filter);
  const under = (p: Player) => `${displayName(p, "admin")} is under ${STATUS_LABEL[p.status]}.`;
  // too many wrong PINs across names: typing one on a player's phone waits (decision 39)
  const claimPaused = t.pins?.claimPausedUntil && t.pins.claimPausedUntil > t.now() ? t.pins.claimPausedUntil : null;

  function everyoneHere() {
    for (const p of notHere) t.send({ type: "player.status", id: p.id, status: "in" });
  }

  function everyoneBack() {
    for (const p of onBreak) t.send({ type: "player.status", id: p.id, status: "in" });
  }

  function add(typed: string): boolean {
    const name = typed.trim();
    if (!name) {
      say("Type a name first.");
      return false;
    }
    const base = playerIdFor(name) || `player-${Date.now().toString(36)}`;
    let id = base;
    for (let i = 2; state.players[id]; i += 1) id = `${base}-${i}`;
    t.send({ type: "player.added", id, name, status: "in" });
    return true;
  }

  return (
    <div className="ad-tab">
      {notHere.length > 0 && (
        <section className="ad-card">
          <h2>Not here yet ({notHere.length})</h2>
          <p className="ad-note">Tap a name when they arrive.</p>
          <div className="ad-notyet" role="group" aria-label="Not here yet">
            {notHere.map((p) => (
              <button key={p.id} type="button" className="ad-chip"
                onClick={() => t.send({ type: "player.status", id: p.id, status: "in" })}>
                {displayName(p, "admin")}
              </button>
            ))}
            <ConfirmButton label="Everyone here" className="ad-chip ad-all" question={`Sure? ${notHere.length} ${notHere.length === 1 ? "player" : "players"} will be marked here.`}
              yes="Mark them here" onConfirm={everyoneHere} />
          </div>
        </section>
      )}

      {onBreak.length > 0 && (
        <section className="ad-card">
          <h2>On a break ({onBreak.length})</h2>
          <p className="ad-note">Tap a name when they are back.</p>
          <div className="ad-notyet" role="group" aria-label="On a break">
            {onBreak.map((p) => (
              <button key={p.id} type="button" className="ad-chip"
                onClick={() => t.send({ type: "player.status", id: p.id, status: "in" })}>
                {displayName(p, "admin")}
              </button>
            ))}
            <ConfirmButton label="Everyone back" className="ad-chip ad-all" question={`Sure? ${onBreak.length} ${onBreak.length === 1 ? "player" : "players"} will be marked here and drawn again.`}
              yes="All back" onConfirm={everyoneBack} />
          </div>
        </section>
      )}

      <section className="ad-card ad-flat ad-everyone">
        {claimPaused && (
          <p className="ad-note" role="status">
            Too many wrong PINs on players' phones. Typing a PIN there waits until {clockTime(claimPaused)}; phones already in carry on.
          </p>
        )}
        <input type="search" value={find} maxLength={24} placeholder="Find a player" aria-label="Find a player" autoComplete="off"
          enterKeyHint="search" onChange={(e) => setFind(e.target.value)} />
        <div className="ad-seg ad-filters" role="group" aria-label="Show">
          {FILTERS.map(({ filter: f, label }) => (
            <button key={f} type="button" className={"ad-chip" + (filter === f ? " on" : "")} aria-pressed={filter === f} onClick={() => setFilter(f)}>
              <span>{label}</span>
              <span className="ad-filter-n">{count(f)}</span>
            </button>
          ))}
        </div>
        {shown.length ? (
          <ul className="ad-list ad-prows">
            {shown.map((p) => (
              <PlayerRow key={p.id} p={p} games={games[p.id] ?? 0} t={t} say={say} pin={t.pins?.players[p.id] ?? NO_ROW} open={openId === p.id}
                onToggle={() => setOpenId(openId === p.id ? null : p.id)} />
            ))}
          </ul>
        ) : wanted && found.length ? (
          <>
            <p className="ad-note">
              {found.length <= 3 ? found.map(under).join(" ") : `${found.length} players match ${find.trim()} under other filters.`}
            </p>
            <button type="button" className="btn ad-wide" onClick={() => setFilter("all")}>
              Look under All
            </button>
          </>
        ) : wanted ? (
          <>
            <p className="ad-note">Nobody on the list is called {find.trim()}.</p>
            <button type="button" className="btn ad-wide" onClick={() => { if (add(find)) { setFind(""); setFilter("all"); } }}>
              Add {find.trim()} as a new player
            </button>
          </>
        ) : (
          <p className="ad-note">Nobody in this group.</p>
        )}
      </section>

      <Fold id="players.add" title="Add a player">
        <form className="ad-row" onSubmit={(e) => { e.preventDefault(); if (add(newName)) setNewName(""); }}>
          <input value={newName} maxLength={24} placeholder="Name" aria-label="New player's name" onChange={(e) => setNewName(e.target.value)} />
          <button type="submit" className="btn">Add</button>
        </form>
        <p className="ad-note">They join as here, ready for the next draw.</p>
      </Fold>
    </div>
  );
}
